import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import {
   Box,
   Typography,
   Container,
   Paper,
   Grid,
   Button,
   CircularProgress,
   Tabs,
   Tab,
   Alert,
   Divider,
   CardMedia,
   CardContent,
   IconButton,
   Card,
   Tooltip,
   Snackbar,
   DialogContent,
   DialogActions,
   Dialog,
   DialogTitle,
   Pagination,
   Checkbox,
} from '@mui/material';
import {
   ArrowBack as ArrowBackIcon,
   Edit as EditIcon,
   Map as MapIcon,
   Photo as PhotoIcon,
   Event as EventIcon,
   People as PeopleIcon,
   FileUpload as UploadIcon,
   LocationOn as LocationOnIcon,
   AutoAwesome as AutoAwesomeIcon,
   Refresh as RefreshIcon,
   PhotoCamera as PhotoCameraIcon,
   Style as StyleIcon,
   Delete as DeleteIcon,
   ArrowForwardIos,
   ArrowBackIos,
   Collections as CollectionsIcon,
   Close as CloseIcon,
} from '@mui/icons-material';
import PhotoLikeButton from '../components/common/PhotoLikeButton';
import {
   doc,
   getDoc,
   updateDoc,
   arrayUnion,
   collection,
   getDocs,
   Timestamp,
   addDoc,
   deleteDoc,
   query,
   where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { format } from 'date-fns';
import PhotoGalleryPopup from '../components/Photos/PhotoGalleryPopup';
import EditTripForm from '../components/TripForms/EditTripForm';
import TripPhotoUploader from '../components/PhotoUpload/TripPhotoUploader';
import TripPhotoGallery from '../components/PhotoUpload/TripPhotoGallery';
import TripMapView from '../components/TripMapView';
import TripParticipantsManager from '../components/TripParticipants/TripParticipantsManager';
import {
   Trip as TripsType,
   Accommodation as AccommodationType,
   TripParticipant,
} from '../types/Trip';
import { useUser } from '../contexts/UserContext';
// import SubscribeButton from '../components/SubscribeButton'; // Hidden - not needed
import sanitizeHtml from 'sanitize-html';
import { getDefaultBackgroundStyle } from '../utils/defaultImages';
import AIGallery from '../components/EventPhotos/AIGallery';
import { hasUploaderMediaIds } from '../components/common/MediaUploaderAttribution';
import { AlbumTimelineUploaderRow } from '../components/common/AlbumTimelineUploaderRow';
import { Carousel } from 'react-material-ui-carousel/dist/components/Carousel';
import { getStorage, ref, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage';
import { get_transformed_photos_by_photo_id, get_trip_details, get_trip_photos } from '../services';

import EditPhotoDialog from '../components/Photos/EditPhotoDialog';
import StyleTransformModal from '../components/PhotoTransform/StyleTransformModal';
import { convertToJPEG } from '../utils/imageUtils';
import LinkedAlbums from '../components/albums/LinkedAlbums';
import ParticipantProfileModal from '../components/ParticipantProfileModal';
import SharePhotosButton from '../components/PhotoSharing/SharePhotosButton';
import { useCanSharePhotos, EntityData } from '../utils/photoSharingPermissions';
// Import action tracking utilities
import {
   trackUserAction,
   trackClick,
   trackContentAction,
   trackQuickError,
   ACTION_TYPES,
   ACTION_CATEGORIES,
   createTripMetadata,
   createPhotoMetadata,
   createPerformanceMetadata,
} from '../utils/actionTracker';

// Local interfaces for component state - these match the Firebase structure
interface LocalAccommodation {
   name: string;
   address: string;
   checkIn: Date;
   checkOut: Date;
   placeId?: string;
   coordinates?: [number, number];
}

interface Photo {
   id: string;
   url: string;
   userId?: string;
   uploadedBy?: string;
   uploadedAt?: Date | { seconds: number; nanoseconds: number } | null;
   title?: string;
   description?: string;
   isEnhanced?: boolean;
   isTransformed?: boolean;
   originalPhotoId?: string;
   transformationType?: string;
   transformationStyle?: string;
   enhancedBy?: string;
   enhancedAt?: Date | { seconds: number; nanoseconds: number } | null;
   tags?: string[];
   isCoverPhoto?: boolean;
   fileName?: string;
   takenAt?: Date | { seconds: number; nanoseconds: number } | null;
   location?: {
      name?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
}

interface LocalTrip {
   id: string;
   userId: string;
   owner: string;
   title: string;
   description: string;
   startDate: Date;
   endDate: Date;
   isOwner?: boolean;
   //  destination: string;
   isHistorical?: boolean;
   //  coordinates?: {
   //     latitude: number;
   //     longitude: number;
   //  };
   primaryDestination?: {
      name: string;
      coordinates: [number, number];
   };
   coverPhotoUrl?: string;
   coverImageUrl?: string;
   participants?: TripParticipant[];
   photos?: Photo[];
   createdAt: Date;
   updatedAt: Date;
   accommodations?: LocalAccommodation[];
   hasCoverPhoto?: boolean;
}

const TripDetailPage = () => {
   const { tripId } = useParams<{ tripId: string }>();
   const navigate = useNavigate();

   // Responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
   const [loading, setLoading] = useState(true);
   const [activeTab, setActiveTab] = useState(0);
   const [error, setError] = useState<string | null>(null);
   const [trip, setTrip] = useState<LocalTrip | null>(null);
   const [isTripOwner, setIsTripOwner] = useState(false);
   const [userRole, setUserRole] = useState<string | null>(null);
   const [editMode, setEditMode] = useState(false);
   const [showPhotoUploader, setShowPhotoUploader] = useState(false);
   const [photoRefreshTrigger, setPhotoRefreshTrigger] = useState(0);
   const [participantsUpdated, setParticipantsUpdated] = useState(0);
   const [tripPhotos, setTripPhotos] = useState<Photo[]>([]);
   const [showUploader, setShowUploader] = useState(false);
   const [selectedPhoto, setSelectedPhoto] = useState<any | null>(null);
   const [styleModalOpen, setStyleModalOpen] = useState(false);
   const [openAIVersion, setOpenAIVersion] = useState<boolean>(false);
   const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
   const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
   const [showEditDialog, setShowEditDialog] = useState(false);
   const [loadingPhotos, setLoadingPhotos] = useState(false);
   const [isEnhancing, setIsEnhancing] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [tripTransformedPhotos, setTripTransformedPhotos] = useState<any[]>([]);
   const [openTransformedPhotoDeleteDialog, setOpenTransformedPhotoDeleteDialog] = useState(false);
   const [transformedPhotoToDelete, setTransformedPhotoToDelete] = useState<Photo | null>(null);
   const [loadingTransformedPhotos, setLoadingTransformedPhotos] = useState(false);

   // View All Photos modal state
   const [viewAllPhotosOpen, setViewAllPhotosOpen] = useState(false);

   // Add contact states
   const [addingContact, setAddingContact] = useState<string>(''); // Store participant ID being added
   const [addedContacts, setAddedContacts] = useState<Set<string>>(new Set()); // Track added contacts

   // Photo gallery popup states
   const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
   const [photoGalleryIndex, setPhotoGalleryIndex] = useState(0);
   const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());

   // Participant profile modal states
   const [participantProfileModalOpen, setParticipantProfileModalOpen] = useState(false);
   const [selectedParticipant, setSelectedParticipant] = useState<any>(null);

   // Prepare entity data for permission checks
   const entityData: EntityData | null = trip
      ? {
           id: trip.id,
           createdBy: (trip as any).createdBy,
           organizerId: (trip as any).organizerId,
           userId: trip.userId,
           members: (trip as any).members,
           participants: trip.participants?.map((p) => ({
              userId: p.userId,
              email: p.email,
              role: p.role as any,
              status: p.status,
           })),
        }
      : null;

   // Check if current user can share photos
   const canShare = useCanSharePhotos(entityData);

   // Photo selection functions
   const togglePhotoSelection = (photoId: string) => {
      setSelectedPhotos((prev) => {
         const newSelection = new Set(prev);
         if (newSelection.has(photoId)) {
            newSelection.delete(photoId);
         } else {
            newSelection.add(photoId);
         }
         return newSelection;
      });
   };

   const selectAllPhotos = () => {
      if (selectedPhotos.size === currentPhotos.length) {
         setSelectedPhotos(new Set());
      } else {
         setSelectedPhotos(new Set(currentPhotos.map((photo: any) => photo.id)));
      }
   };

   // Function to add participant to relationship network
   const addToContacts = async (participant: any) => {
      if (
         !currentUser ||
         addingContact === (participant.userId || participant.email) ||
         !participant.email
      )
         return;

      setAddingContact(participant.userId || participant.email);

      try {
         // Check if relationship already exists (both directions)
         const relationshipNetworkRef = collection(db, 'relationship_network');

         // Query 1: Where current user is the initiator
         const q1 = query(
            relationshipNetworkRef,
            where('userId', '==', currentUser.uid),
            where('relationToEmail', '==', participant.email),
            where('status', '==', 'accepted')
         );

         // Query 2: Where current user is the recipient
         const q2 = query(
            relationshipNetworkRef,
            where('relationToUserId', '==', currentUser.uid),
            where('recipientEmail', '==', participant.email),
            where('status', '==', 'accepted')
         );

         const [querySnapshot1, querySnapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);

         if (!querySnapshot1.empty || !querySnapshot2.empty) {
            setSnackbarMessage('This person is already in your contacts');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
         }

         // Create new relationship
         const relationshipData = {
            userId: currentUser.uid,
            relationToUserId: participant.userId || null,
            relationToEmail: participant.email,
            relationType: 'friend', // Default relationship type
            relationLabel: 'Trip Participant',
            notes: `Connected through trip: ${trip?.title || 'Trip'}`,
            status: 'accepted',
            isNetworkConfirmed: true,
            source: 'trip_participant',
            sourceId: trip?.id,
            createdAt: new Date(),
            updatedAt: new Date(),
            // Store contact details for better display
            recipientDisplayName: participant.displayName || participant.email.split('@')[0],
            recipientEmail: participant.email,
            recipientPhotoURL: participant.photoURL || '',
         };

         await addDoc(relationshipNetworkRef, relationshipData);

         // Update UI state
         setAddedContacts(
            (prev) => new Set([...Array.from(prev), participant.userId || participant.email])
         );

         // Update the trip participant's isInNetwork status
         setTrip((prevTrip) => {
            if (!prevTrip || !prevTrip.participants) return prevTrip;

            const updatedParticipants = prevTrip.participants.map((p: any) => {
               if (p.userId === participant.userId || p.email === participant.email) {
                  return { ...p, isInNetwork: true };
               }
               return p;
            });

            return { ...prevTrip, participants: updatedParticipants };
         });

         setSnackbarMessage(
            `${participant.displayName || participant.email} added to your contacts!`
         );
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error adding contact:', error);
         setSnackbarMessage('Failed to add contact. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setAddingContact('');
      }
   };

   // Helper function to get correct tab indices - participants tab now visible to all users
   const getTabIndices = () => {
      return {
         participants: 0,
         photos: 1,
         map: 2,
         albums: 3,
      };
   };

   // Effect to reset tab when user role changes
   useEffect(() => {
      // No longer need to reset tabs based on user role since all users can see participants
   }, [userRole]);
   const [currentPage, setCurrentPage] = useState(1);
   const [photosPerPage] = useState(8); // Show 8 photos per page

   const { currentUser } = useUser();

   // Pagination calculations
   const totalPages = Math.ceil(tripPhotos.length / photosPerPage);
   const startIndex = (currentPage - 1) * photosPerPage;
   const endIndex = startIndex + photosPerPage;
   const currentPhotos = tripPhotos.slice(startIndex, endIndex);

   // Pagination handlers
   const handleNextPage = () => {
      if (currentPage < totalPages) {
         setCurrentPage((prev) => prev + 1);
      }
   };

   const handlePrevPage = () => {
      if (currentPage > 1) {
         setCurrentPage((prev) => prev - 1);
      }
   };

   const handlePageChange = (page: number) => {
      setCurrentPage(page);
   };

   const handleCloseViewAllPhotos = () => {
      setViewAllPhotosOpen(false);
      setCurrentPage(1); // Reset to first page when closing
   };

   const handleSnackbarClose = () => {
      setSnackbarOpen(false);
   };

   // Function to handle opening participant profile modal
   const handleOpenParticipantProfile = (participant: any) => {
      setSelectedParticipant(participant);
      setParticipantProfileModalOpen(true);
   };

   // Function to handle closing participant profile modal
   const handleCloseParticipantProfile = () => {
      setParticipantProfileModalOpen(false);
      setSelectedParticipant(null);
   };

   const fetchTransformedPhotos = async () => {
      try {
         setLoadingTransformedPhotos(true);
         const transformedPhotos = await get_transformed_photos_by_photo_id(selectedPhoto.id || '');
         setTripTransformedPhotos(transformedPhotos);
      } catch (err) {
      } finally {
         setLoadingTransformedPhotos(false);
      }
   };

   const formatEventDate = (date: any) => {
      try {
         return date?.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
         });
      } catch (err) {
         return 'Invalid date';
      }
   };

   const loadTripPhotos = async () => {
      try {
         setLoadingPhotos(true);

         // Early return if no tripId or currentUser
         if (!tripId || !currentUser?.uid) {
            console.log('Missing tripId or currentUser, skipping photo load');
            setTripPhotos([]);
            return;
         }

         const data = await get_trip_photos(tripId, currentUser.uid);
         console.log('Trip photos data received:', data);

         if (data && Array.isArray(data)) {
            setTripPhotos(
               data.map((photo: any) => {
                  console.log(
                     'Processing trip photo:',
                     photo.id,
                     'likeCount:',
                     photo.likeCount,
                     'isLiked:',
                     photo.isLiked
                  );
                  return {
                     ...photo,
                     id: photo.id,
                     url: photo.url || photo.thumbnailUrl || '',
                     title: photo?.title || 'Untitled',
                     description: photo?.description || '',
                     likeCount: photo.likeCount || 0,
                     isLiked: photo.isLiked || false,
                     takenAt: photo?.takenAt
                        ? (() => {
                             const date = new Date(photo.takenAt);
                             return isNaN(date.getTime()) ? null : date;
                          })()
                        : null,
                     uploadedAt: photo?.uploadedAt
                        ? (() => {
                             const date = new Date(photo.uploadedAt);
                             return isNaN(date.getTime()) ? null : date;
                          })()
                        : null,
                     createdAt: photo?.createdAt
                        ? (() => {
                             const date = new Date(photo.createdAt);
                             return isNaN(date.getTime()) ? null : date;
                          })()
                        : null,
                     location: photo?.location || null,
                  };
               })
            );
         } else {
            console.log('No photos data or invalid format:', data);
            setTripPhotos([]);
         }
      } catch (err) {
         console.error('Error loading trip photos:', err);
         setTripPhotos([]);
      } finally {
         setLoadingPhotos(false);
      }
   };

   const handleDeletePhoto = (photo: any) => {
      setPhotoToDelete(photo);
      setOpenDeleteDialog(true);
   };

   const handleCloseEditDialog = (photo: any) => {
      setShowEditDialog(true);
      setSelectedPhoto(photo);
   };

   const handleDeleteCancel = () => {
      setOpenDeleteDialog(false);
      setPhotoToDelete(null);
   };

   const handleTransformedPhotoDelete = (photo: any) => {
      setTransformedPhotoToDelete(photo);
      setOpenTransformedPhotoDeleteDialog(true);
   };

   const handleDeleteConfirm = async () => {
      if (!photoToDelete) return;

      const photoId = photoToDelete.id;
      const photoTitle = photoToDelete.title || 'Unknown';

      try {
         // Track photo deletion attempt
         await trackUserAction({
            actionType: ACTION_TYPES.DELETE_PHOTO,
            category: ACTION_CATEGORIES.PHOTO,
            description: `Deleted photo from trip: ${photoTitle}`,
            targetId: photoId,
            targetType: 'photo',
            metadata: createPhotoMetadata('delete_trip_photo', photoId, photoTitle, {
               source: 'trip_detail_page',
               context: 'photo_management',
               associatedTripId: tripId,
               tripTitle: trip?.title || 'Unknown',
               uploadedBy: photoToDelete.uploadedBy || 'Unknown',
               isEnhanced: photoToDelete.isEnhanced || false,
               isTransformed: photoToDelete.isTransformed || false,
            }),
         });
      } catch (trackingError) {
         console.warn('Failed to track photo deletion:', trackingError);
      }

      try {
         // Delete from storage
         const storage = getStorage();
         const imageRef = ref(
            storage,
            `photos/${photoToDelete.uploadedBy}/${photoToDelete.fileName}`
         );
         await deleteObject(imageRef);

         // Delete from Firestore
         await deleteDoc(doc(db, 'photos', photoToDelete.id));

         // Remove from local state
         setTripPhotos((prev) => prev.filter((photo) => photo.id !== photoToDelete.id));

         setSnackbarSeverity('success');
         setSnackbarMessage('Photo deleted successfully');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error deleting photo:', error);

         // Track photo deletion error
         try {
            await trackQuickError(
               error instanceof Error ? error : new Error('Photo deletion failed'),
               'trip_photo_deletion',
               {
                  photoId,
                  photoTitle,
                  tripId: tripId,
                  tripTitle: trip?.title || 'Unknown',
                  action: 'delete_trip_photo_error',
               }
            );
         } catch (trackingError) {
            console.warn('Failed to track photo deletion error:', trackingError);
         }

         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to delete photo');
         setSnackbarOpen(true);
      } finally {
         setOpenDeleteDialog(false);
         setPhotoToDelete(null);
      }
   };

   const handleDeleteTransformedPhotoConfirm = async () => {
      try {
         if (!transformedPhotoToDelete) return;
         const photoRef = doc(db, 'photos', transformedPhotoToDelete?.id || '');
         await deleteDoc(photoRef);
         setTripTransformedPhotos((prev: any[]): any[] => [
            ...prev.filter(
               (p: any) =>
                  p.id != transformedPhotoToDelete?.id || p.url != transformedPhotoToDelete?.url
            ),
         ]);

         setSnackbarSeverity('success');
         setSnackbarMessage('Photo deleted successfully');
         setSnackbarOpen(true);
      } catch (err) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to delete photo');
         setSnackbarOpen(true);
      } finally {
         setOpenTransformedPhotoDeleteDialog(false);
         setTransformedPhotoToDelete(null);
      }
   };

   const fetchTripData = async () => {
      if (!tripId) return;

      const viewStartTime = Date.now();

      try {
         setLoading(true);
         setError(null);

         const trip = (await get_trip_details(tripId)) as any;
         const startDate = trip.startDate
            ? (() => {
                 const date = new Date(trip.startDate);
                 return isNaN(date.getTime()) ? null : date;
              })()
            : null;
         const endDate = trip.endDate
            ? (() => {
                 const date = new Date(trip.endDate);
                 return isNaN(date.getTime()) ? null : date;
              })()
            : null;
         const isOwner = trip.userId == currentUser?.uid;
         const isParticipant = trip?.participants?.some(
            (participant: any) => participant.userId == currentUser?.uid
         );

         setIsTripOwner(isOwner || isParticipant);

         // Determine user's role in the trip
         let currentUserRole = null;

         // Check members array first (primary structure from backend API)
         if (trip?.members && Array.isArray(trip.members)) {
            const currentUserMember = trip.members.find(
               (member: any) => member.userId === currentUser?.uid
            );
            if (currentUserMember) {
               currentUserRole = currentUserMember.role;
            }
         }

         // Fallback to participants array if no member found
         if (!currentUserRole && trip?.participants && Array.isArray(trip.participants)) {
            const currentUserParticipant = trip.participants.find(
               (participant: any) => participant.userId === currentUser?.uid
            );
            if (currentUserParticipant) {
               currentUserRole = currentUserParticipant.role;
            }
         }

         // Final fallback - if user is trip creator but not in members, make them owner
         if (!currentUserRole && isOwner) {
            currentUserRole = 'owner';
         }

         // Default to viewer if no role found
         if (!currentUserRole) {
            currentUserRole = 'viewer';
         }
         setUserRole(currentUserRole);

         // Check relationship status for participants if current user exists
         let updatedParticipants = trip.participants || [];
         if (currentUser && updatedParticipants.length > 0) {
            try {
               // Get all relationships for the current user (both directions)
               const relationshipNetworkRef = collection(db, 'relationship_network');

               // Query 1: Where current user is the initiator
               const q1 = query(
                  relationshipNetworkRef,
                  where('userId', '==', currentUser.uid),
                  where('status', '==', 'accepted')
               );

               // Query 2: Where current user is the recipient (reverse relationships)
               const q2 = query(
                  relationshipNetworkRef,
                  where('relationToUserId', '==', currentUser.uid),
                  where('status', '==', 'accepted')
               );

               const [querySnapshot1, querySnapshot2] = await Promise.all([
                  getDocs(q1),
                  getDocs(q2),
               ]);

               const relationships1 = querySnapshot1.docs.map((doc) => doc.data());
               const relationships2 = querySnapshot2.docs.map((doc) => doc.data());
               const allRelationships = [...relationships1, ...relationships2];

               // Update participants with relationship status
               updatedParticipants = updatedParticipants.map((participant: any) => {
                  // Check if this participant is in the current user's network (both directions)
                  const isInNetwork = allRelationships.some((rel) => {
                     // Check forward relationship (current user -> participant)
                     const forwardMatch =
                        rel.userId === currentUser.uid &&
                        (rel.relationToUserId === participant.userId ||
                           rel.relationToEmail === participant.email);

                     // Check reverse relationship (participant -> current user)
                     const reverseMatch =
                        rel.relationToUserId === currentUser.uid &&
                        (rel.userId === participant.userId ||
                           rel.recipientEmail === participant.email);

                     return forwardMatch || reverseMatch;
                  });

                  return {
                     ...participant,
                     isInNetwork: isInNetwork,
                  };
               });
            } catch (relationshipError) {
               console.error('Error fetching relationship status:', relationshipError);
               // Continue without relationship status if there's an error
            }
         }

         const tripData = {
            ...trip,
            id: tripId,
            userId: trip.userId,
            title: trip.title || 'Untitled Trip',
            description: trip.description || '',
            startDate: trip.startDate
               ? (() => {
                    const date = new Date(trip.startDate);
                    return isNaN(date.getTime()) ? null : date;
                 })()
               : null,
            endDate: trip.endDate
               ? (() => {
                    const date = new Date(trip.endDate);
                    return isNaN(date.getTime()) ? null : date;
                 })()
               : null,
            isHistorical: trip.isHistorical || false,
            coverPhotoUrl: trip.coverPhotoUrl || trip.coverImageUrl || null,
            participants: updatedParticipants,
            createdAt: trip.createdAt
               ? (() => {
                    const date = new Date(trip.createdAt);
                    return isNaN(date.getTime()) ? new Date() : date;
                 })()
               : new Date(),
            updatedAt: trip.updatedAt
               ? (() => {
                    const date = new Date(trip.updatedAt);
                    return isNaN(date.getTime()) ? new Date() : date;
                 })()
               : new Date(),
            accommodations: trip.accommodations
               ? trip.accommodations.map((acc: any) => ({
                    ...acc,
                    checkIn: acc.checkIn
                       ? (() => {
                            const date = new Date(acc.checkIn);
                            return isNaN(date.getTime()) ? null : date;
                         })()
                       : null,
                    checkOut: acc.checkOut
                       ? (() => {
                            const date = new Date(acc.checkOut);
                            return isNaN(date.getTime()) ? null : date;
                         })()
                       : null,
                 }))
               : [],
            hasCoverPhoto: trip.hasCoverPhoto || !!trip.coverPhotoUrl || !!trip.coverImageUrl,
         };

         setTrip(tripData);

         // Track trip detail page view
         const viewEndTime = Date.now();
         const loadDuration = (viewEndTime - viewStartTime) / 1000;

         try {
            await trackContentAction(ACTION_TYPES.VIEW_CONTENT, tripId, 'trip', {
               tripTitle: tripData.title,
               isHistorical: tripData.isHistorical,
               isOwner,
               isParticipant,
               hasDescription: !!tripData.description?.trim(),
               hasCoverPhoto: !!tripData.coverPhotoUrl,
               participantCount: tripData.participants?.length || 0,
               accommodationCount: tripData.accommodations?.length || 0,
               tripDuration:
                  tripData.startDate && tripData.endDate
                     ? Math.ceil(
                          (tripData.endDate.getTime() - tripData.startDate.getTime()) /
                             (1000 * 60 * 60 * 24)
                       )
                     : 0,
               loadDuration,
               createdAt: tripData.createdAt ? tripData.createdAt.toISOString() : null,
               destination: tripData.primaryDestination?.name || 'Unknown',
               action: 'view_trip_detail',
            });
         } catch (trackingError) {
            console.warn('Failed to track trip view:', trackingError);
         }
      } catch (err) {
         console.error('Error fetching trip data:', err);
         setError('Error fetching trip data');

         // Track trip loading error
         try {
            await trackQuickError(
               err instanceof Error ? err : new Error('Trip loading failed'),
               'trip_detail_load',
               {
                  tripId,
                  loadDuration: (Date.now() - viewStartTime) / 1000,
                  action: 'trip_detail_load_error',
               }
            );
         } catch (trackingError) {
            console.warn('Failed to track trip loading error:', trackingError);
         }
      } finally {
         setLoading(false);
      }
   };

   const onPhotoEnhanced = async (images: any[], photoId: string) => {
      let totalFails = 0;
      let totalSuccess = 0;
      let totalSaved = 0;
      setIsEnhancing(true);
      const photos = images.map((image: any) => {
         return {
            ...image,
            transformedUrl: image?.url,
            basePhotoId: photoId,
         };
      });

      try {
         await Promise.all(
            photos.map(async (photo: any) => {
               try {
                  await handleSave(photo);
                  totalSuccess++;
               } catch (err) {
                  totalFails++;
               }
            })
         );

         setSnackbarSeverity('success');
         setSnackbarMessage(
            `${totalSuccess} photos saved successfully, ${totalFails} photos failed`
         );
         setSnackbarOpen(true);
      } catch (err) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to enhance photo');
         setSnackbarOpen(true);
      } finally {
         setIsEnhancing(false);
         setSelectedPhoto(null);
      }
   };

   const handleSave = async (photo: any) => {
      if (!currentUser?.uid) {
         return;
      }

      try {
         if (!photo) {
            setSnackbarSeverity('error');
            setSnackbarMessage('Photo not found');
            setSnackbarOpen(true);
            return;
         }

         if (photo?.id) {
            const photoRef = doc(db, 'photos', photo?.id || '');
            const photoDoc = await getDoc(photoRef);
            if (photoDoc.exists()) {
               setSnackbarSeverity('error');
               setSnackbarMessage('Photo already exists');
               setSnackbarOpen(true);
               return;
            }
         }

         const { blob, extension } = await convertToJPEG(photo?.transformedUrl);

         // Generate unique filename for storage
         const storageFileName = `transformed_${Date.now()}_${Math.random().toString(36).substring(2)}`;
         const path = `transformed_photos/${currentUser?.uid}/${storageFileName}.${extension}`;

         // Upload to Firebase Storage
         const storage = getStorage();
         const storageRef = ref(storage, path);

         // Upload file
         await uploadBytes(storageRef, blob);

         const photoUrl = await getDownloadURL(storageRef);

         const newPhoto = {
            url: photoUrl,
            title: `Enhanced - ${selectedPhoto?.title || 'Photo'}`,
            description: `AI enhanced version of ${selectedPhoto?.title || 'photo'}`,
            fileName: `${storageFileName}.${extension}`,
            size: blob.size,
            mimeType: blob.type,
            isTransformed: true,
            userId: currentUser?.uid,
            uploadedBy: currentUser?.uid,
            type: 'transformed_photo',
            basePhotoId: photo.basePhotoId || selectedPhoto?.id || '',
            createdAt: Timestamp.now(),
            uploadedAt: Timestamp.now(),
            takenAt: selectedPhoto?.takenAt
               ? Timestamp.fromDate(new Date(selectedPhoto.takenAt))
               : Timestamp.now(),
            tripId: tripId || '',
            tags: [],
            likeCount: 0,
            likedBy: [],
         };

         const photoRef = await addDoc(collection(db, 'photos'), newPhoto);

         setSnackbarSeverity('success');
         setSnackbarMessage('Photo saved successfully');
         setSnackbarOpen(true);
      } catch (err) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to upload photo');
         setSnackbarOpen(true);
      } finally {
      }
   };

   const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
      const previousTab = activeTab;

      // Participants tab is now accessible to all users (viewers, editors, owners)

      setActiveTab(newValue);

      // Track tab change in trip detail
      const tabNames = ['Participants', 'Photos', 'Map', 'Albums'];
      try {
         await trackUserAction({
            actionType: ACTION_TYPES.CLICK,
            category: ACTION_CATEGORIES.USER_INTERACTION,
            description: `Switched to ${tabNames[newValue]} tab in trip detail`,
            targetId: tripId,
            targetType: 'trip',
            metadata: createTripMetadata('trip_tab_change', tripId, trip?.title || 'Unknown', {
               source: 'trip_detail_page',
               context: 'navigation',
               fromTab: tabNames[previousTab] || 'Unknown',
               toTab: tabNames[newValue] || 'Unknown',
               fromTabIndex: previousTab,
               toTabIndex: newValue,
            }),
         });
      } catch (error) {
         console.warn('Failed to track tab change:', error);
      }

      if (newValue === 1) {
         // Photos tab
         loadTripPhotos();
         fetchTransformedPhotos();
      }

      if (newValue === 0) {
         // Participants tab - refresh participants data
         setParticipantsUpdated((prev) => prev + 1);
      }
   };

   const handleEditClick = async () => {
      setEditMode(true);

      // Track trip edit start
      try {
         await trackUserAction({
            actionType: 'edit_trip_start',
            category: ACTION_CATEGORIES.TRIP,
            description: `Started editing trip: ${trip?.title}`,
            targetId: tripId,
            targetType: 'trip',
            metadata: createTripMetadata('edit_trip_start', tripId, trip?.title || 'Unknown', {
               source: 'trip_detail_page',
               context: 'trip_editing',
            }),
         });
      } catch (error) {
         console.warn('Failed to track trip edit start:', error);
      }
   };

   const handleEditClose = () => {
      setEditMode(false);
      // Refresh trip data after editing
      if (tripId) {
         fetchTripData();
      }
   };

   const handlePhotoUploadComplete = () => {
      // Trigger a refresh of the photo gallery
      setPhotoRefreshTrigger((prev) => prev + 1);
      // Close the uploader
      setShowPhotoUploader(false);
   };

   const handleParticipantsUpdated = () => {
      setParticipantsUpdated((prev) => prev + 1);
      // Refresh trip data to update participants
      fetchTripData();
   };

   const formatDateRange = (startDate: Date | null, endDate: Date | null) => {
      if (!startDate || !endDate) {
         return 'Date not specified';
      }

      // Validate that dates are valid Date objects
      const start = startDate instanceof Date ? startDate : new Date(startDate);
      const end = endDate instanceof Date ? endDate : new Date(endDate);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
         return 'Invalid date';
      }

      return `${format(start, 'MMMM d, yyyy')} - ${format(end, 'MMMM d, yyyy')}`;
   };

   // Convert LocalTrip to TripsType for the TripParticipantsManager component
   const getTripsTypeObject = (): TripsType => {
      if (!trip) {
         throw new Error('Trip not found');
      }

      // Add debugging to check owner ID

      // For debugging purposes, log the currentUser and whether it should be owner
      const isCurrentUserOwner = currentUser?.uid === trip.userId;

      return {
         id: trip.id,
         title: trip.title,
         description: trip.description,
         startDate: trip.startDate,
         endDate: trip.endDate,
         userId: trip.userId, // The owner ID
         createdAt: trip.createdAt,
         updatedAt: trip.updatedAt,
         coverPhoto: trip.coverPhotoUrl || trip.coverImageUrl,
         isPublic: false,
         participants: trip.participants,
         locations: trip.accommodations?.map((acc) => ({
            name: acc.name,
            coordinates: acc.coordinates
               ? ([acc.coordinates[0], acc.coordinates[1]] as [number, number])
               : ([0, 0] as [number, number]),
         })),
      };
   };

   useEffect(() => {
      fetchTripData();
   }, [tripId]);

   useEffect(() => {
      if (tripId && currentUser) {
         loadTripPhotos();
      }
   }, [
      tripId,
      currentUser,
      showEditDialog,
      isEnhancing,
      styleModalOpen,
      activeTab,
      openTransformedPhotoDeleteDialog,
      openDeleteDialog,
   ]);

   useEffect(() => {
      if (selectedPhoto && openAIVersion) {
         fetchTransformedPhotos();
      }
   }, [selectedPhoto, openAIVersion]);

   if (loading) {
      return (
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'center',
               alignItems: 'center',
               height: '80vh',
            }}
         >
            <CircularProgress />
         </Box>
      );
   }

   if (error || !trip) {
      return (
         <Container maxWidth='lg' sx={{ py: 4 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/trips')} sx={{ mb: 2 }}>
               Back to Trips
            </Button>

            <Alert severity='error' sx={{ mt: 2 }}>
               {error || 'Trip not found'}
            </Alert>
         </Container>
      );
   }

   return (
      <Box
         sx={{
            minHeight: '100vh',
            bgcolor: isMobile ? 'grey.50' : 'background.default',
            pb: isMobile ? 8 : 0,
            width: '100%',
            overflow: 'hidden',
         }}
      >
         {/* Mobile Header with gradient background */}
         {isMobile && (
            <Box
               sx={{
                  background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                  minHeight: '200px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
               }}
            >
               {/* Back button */}
               <Box sx={{ p: 2, pb: 1 }}>
                  <Button
                     startIcon={<ArrowBackIcon />}
                     onClick={() => navigate('/trips')}
                     size='small'
                     sx={{
                        fontSize: '0.875rem',
                        color: 'white',
                        '&:hover': {
                           backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                     }}
                  >
                     Back
                  </Button>
               </Box>

               {/* Trip content */}
               <Box
                  sx={{
                     px: 2,
                     pb: 2,
                     flexGrow: 1,
                     display: 'flex',
                     flexDirection: 'column',
                     justifyContent: 'space-between',
                  }}
               >
                  <Box>
                     <Typography
                        variant='h4'
                        component='h1'
                        sx={{
                           color: 'white',
                           mb: 2,
                           fontSize: '1.5rem',
                           fontWeight: 'bold',
                           lineHeight: 1.2,
                           textAlign: 'center',
                        }}
                     >
                        {trip.title}
                     </Typography>

                     {/* Date range */}
                     <Typography
                        variant='body2'
                        sx={{
                           color: 'white',
                           textAlign: 'center',
                           mb: 1,
                           fontSize: '0.875rem',
                        }}
                     >
                        {formatDateRange(trip.startDate, trip.endDate)}
                     </Typography>

                     {/* Location with pin icon */}
                     {trip.primaryDestination && (
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 2,
                           }}
                        >
                           <LocationOnIcon sx={{ color: 'white', mr: 0.5, fontSize: '16px' }} />
                           <Typography
                              variant='body2'
                              sx={{ color: 'white', fontSize: '0.875rem' }}
                           >
                              {trip.primaryDestination.name}
                           </Typography>
                        </Box>
                     )}
                  </Box>
               </Box>
            </Box>
         )}

         {/* Desktop Header */}
         {!isMobile && (
            <Container maxWidth='lg' sx={{ py: 4, px: 3 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Button
                     startIcon={<ArrowBackIcon />}
                     onClick={() => navigate('/trips')}
                     sx={{ mr: 2 }}
                  >
                     Back to trips
                  </Button>
               </Box>

               <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                     <Box>
                        <Typography variant='h4' component='h1' gutterBottom>
                           {trip.title}
                        </Typography>
                        <Typography variant='subtitle1' color='text.secondary'>
                           {formatDateRange(trip.startDate, trip.endDate)}
                        </Typography>
                        {trip.primaryDestination && (
                           <Typography variant='body1' sx={{ mt: 1 }}>
                              <LocationOnIcon
                                 sx={{ fontSize: '1rem', verticalAlign: 'middle', mr: 0.5 }}
                              />
                              {trip.primaryDestination.name}
                           </Typography>
                        )}
                     </Box>

                     <Box>
                        {(userRole === 'owner' || userRole === 'editor') && (
                           <Button
                              variant='outlined'
                              startIcon={<EditIcon />}
                              onClick={handleEditClick}
                           >
                              Edit Trip
                           </Button>
                        )}
                     </Box>
                  </Box>

                  {trip.description && (
                     <Typography
                        variant='body1'
                        paragraph
                        sx={{ mt: 2 }}
                        dangerouslySetInnerHTML={{
                           __html: sanitizeHtml(trip.description),
                        }}
                     />
                  )}
               </Paper>

               {/* Accommodations section - add this if you want to display accommodations in the trip detail view */}
               {trip.accommodations && trip.accommodations.length > 0 && (
                  <Paper
                     sx={{
                        p: isMobile ? 2 : 3,
                        mb: isMobile ? 2 : 3,
                        borderRadius: isMobile ? 2 : 1,
                        boxShadow: isMobile ? 1 : 2,
                     }}
                  >
                     <Typography
                        variant={isMobile ? 'subtitle1' : 'h6'}
                        gutterBottom
                        sx={{
                           fontSize: isMobile ? '1rem' : '1.25rem',
                           fontWeight: 600,
                        }}
                     >
                        Accommodations
                     </Typography>
                     <Box sx={{ mt: isMobile ? 1.5 : 2 }}>
                        {trip.accommodations.map((acc, index) => (
                           <Box
                              key={index}
                              sx={{
                                 mb: isMobile ? 1.5 : 2,
                                 pb: isMobile ? 1.5 : 2,
                                 borderBottom:
                                    index < trip.accommodations!.length - 1
                                       ? '1px solid #eee'
                                       : 'none',
                              }}
                           >
                              <Typography
                                 variant={isMobile ? 'body1' : 'subtitle1'}
                                 fontWeight='bold'
                                 sx={{
                                    fontSize: isMobile ? '0.9rem' : '1rem',
                                    mb: 0.5,
                                 }}
                              >
                                 {acc.name}
                              </Typography>
                              {acc.address && (
                                 <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{
                                       fontSize: isMobile ? '0.8rem' : '0.875rem',
                                       lineHeight: 1.4,
                                    }}
                                 >
                                    {acc.address}
                                 </Typography>
                              )}
                              {acc.checkIn && acc.checkOut && (
                                 <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{
                                       mt: isMobile ? 0.5 : 1,
                                       fontSize: isMobile ? '0.8rem' : '0.875rem',
                                    }}
                                 >
                                    {(() => {
                                       try {
                                          const checkIn =
                                             acc.checkIn instanceof Date
                                                ? acc.checkIn
                                                : new Date(acc.checkIn);
                                          const checkOut =
                                             acc.checkOut instanceof Date
                                                ? acc.checkOut
                                                : new Date(acc.checkOut);

                                          if (
                                             isNaN(checkIn.getTime()) ||
                                             isNaN(checkOut.getTime())
                                          ) {
                                             return 'Invalid dates';
                                          }

                                          return `${format(checkIn, 'MMM d, yyyy')} - ${format(checkOut, 'MMM d, yyyy')}`;
                                       } catch (error) {
                                          return 'Date formatting error';
                                       }
                                    })()}
                                 </Typography>
                              )}
                           </Box>
                        ))}
                     </Box>
                  </Paper>
               )}
            </Container>
         )}

         {/* Mobile Tabs */}
         {isMobile ? (
            <Box
               sx={{
                  bgcolor: 'white',
                  borderBottom: '1px solid #e0e0e0',
                  boxShadow: 'none',
               }}
            >
               <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant='scrollable'
                  scrollButtons='auto'
                  allowScrollButtonsMobile
                  sx={{
                     '& .MuiTab-root': {
                        minWidth: 'auto',
                        fontSize: '0.875rem',
                        padding: '12px 16px',
                        minHeight: 48,
                        fontWeight: 500,
                        textTransform: 'none',
                        color: 'text.secondary',
                        '&.Mui-selected': {
                           color: 'primary.main',
                           fontWeight: 600,
                        },
                     },
                     '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: 0,
                        backgroundColor: 'primary.main',
                     },
                  }}
               >
                  <Tab icon={<PeopleIcon sx={{ fontSize: '20px' }} />} label='People' />
                  <Tab icon={<PhotoIcon sx={{ fontSize: '20px' }} />} label='Photos' />
                  <Tab icon={<MapIcon sx={{ fontSize: '20px' }} />} label='Map' />
                  <Tab icon={<CollectionsIcon sx={{ fontSize: '20px' }} />} label='Albums' />
               </Tabs>
            </Box>
         ) : (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
               <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  aria-label='trip details tabs'
                  variant='fullWidth'
                  sx={{
                     '& .MuiTab-root': {
                        fontSize: '0.875rem',
                        padding: '12px 16px',
                     },
                  }}
               >
                  <Tab icon={<PeopleIcon />} label='Participants' />
                  <Tab icon={<PhotoIcon />} label='Photos' />
                  <Tab icon={<MapIcon />} label='Map' />
                  <Tab icon={<CollectionsIcon />} label='Albums' />
               </Tabs>
            </Box>
         )}

         {/* Tab Content */}
         <Box
            sx={{
               mt: isMobile ? 0 : 2,
               px: isMobile ? 0 : 0,
               pb: isMobile ? 2 : 0,
               bgcolor: isMobile ? 'background.paper' : 'transparent',
            }}
         >
            {/* Participants tab - now visible to all users */}
            {activeTab === getTabIndices().participants && (
               <Box sx={{ px: isMobile ? 2 : 0 }}>
                  {trip && (
                     <>
                        <TripParticipantsManager
                           trip={getTripsTypeObject()}
                           onParticipantsUpdated={handleParticipantsUpdated}
                           userRole={userRole || 'viewer'}
                           onAddToContacts={addToContacts}
                           addingContact={addingContact}
                           addedContacts={addedContacts}
                           onOpenParticipantProfile={handleOpenParticipantProfile}
                        />
                     </>
                  )}
               </Box>
            )}

            {/* Photos tab */}
            {activeTab === getTabIndices().photos && (
               <Box>
                  {/* Mobile-optimized header section */}
                  {isMobile ? (
                     <Box sx={{ mb: 2 }}>
                        <Typography
                           variant='h6'
                           component='h2'
                           sx={{
                              fontSize: '1.125rem',
                              fontWeight: 600,
                              color: 'text.primary',
                              mb: 2,
                           }}
                        >
                           Trip Photos
                        </Typography>

                        {/* Mobile buttons layout */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                           {tripPhotos.length > 0 && (
                              <Button
                                 variant='outlined'
                                 startIcon={<CollectionsIcon sx={{ fontSize: '16px' }} />}
                                 onClick={() => setViewAllPhotosOpen(true)}
                                 fullWidth
                                 sx={{
                                    py: 1.5,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    borderColor: '#00C4A7',
                                    color: '#00C4A7',
                                    '&:hover': {
                                       borderColor: '#00C4A7',
                                       backgroundColor: 'rgba(0, 196, 167, 0.04)',
                                    },
                                 }}
                              >
                                 View All ({tripPhotos.length})
                              </Button>
                           )}
                           <Button
                              variant='contained'
                              startIcon={<PhotoCameraIcon sx={{ fontSize: '16px' }} />}
                              onClick={() => setShowUploader((prev) => !prev)}
                              fullWidth
                              sx={{
                                 py: 1.5,
                                 borderRadius: '12px',
                                 textTransform: 'none',
                                 fontSize: '0.875rem',
                                 fontWeight: 600,
                                 background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                                 '&:hover': {
                                    background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                                 },
                              }}
                           >
                              {showUploader ? 'Hide Uploader' : 'Add Photos'}
                           </Button>
                        </Box>
                     </Box>
                  ) : (
                     <Box
                        sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           alignItems: 'center',
                           mb: 2,
                        }}
                     >
                        <Typography
                           variant='h5'
                           component='h2'
                           sx={{
                              fontSize: '1.5rem',
                              fontWeight: 600,
                           }}
                        >
                           Trip Photos
                        </Typography>

                        <Box
                           sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
                        >
                           {tripPhotos.length > 0 && (
                              <>
                                 <Button
                                    variant='outlined'
                                    startIcon={<CollectionsIcon sx={{ fontSize: '1.25rem' }} />}
                                    onClick={() => setViewAllPhotosOpen(true)}
                                    sx={{
                                       fontSize: '0.875rem',
                                       py: 1,
                                       px: 2,
                                       borderRadius: 1,
                                    }}
                                 >
                                    View All ({tripPhotos.length})
                                 </Button>
                                 {selectedPhotos.size > 0 && (
                                    <>
                                       <Button
                                          variant='text'
                                          onClick={selectAllPhotos}
                                          size='small'
                                          sx={{ fontSize: '0.75rem' }}
                                       >
                                          {selectedPhotos.size === currentPhotos.length
                                             ? 'Deselect All'
                                             : 'Select All'}
                                       </Button>
                                       <Typography
                                          variant='body2'
                                          color='primary'
                                          sx={{ fontWeight: 'bold' }}
                                       >
                                          {selectedPhotos.size} photo
                                          {selectedPhotos.size !== 1 ? 's' : ''} selected
                                       </Typography>
                                    </>
                                 )}
                                 <SharePhotosButton
                                    entityType='trip'
                                    entityId={tripId!}
                                    entityTitle={trip?.title || 'Trip'}
                                    entityData={entityData}
                                    selectedPhotos={Array.from(selectedPhotos)
                                       .map((id) => tripPhotos.find((p) => p.id === id))
                                       .filter(Boolean)}
                                    variant='outlined'
                                    sx={{
                                       fontSize: '0.875rem',
                                       py: 1,
                                       px: 2,
                                       borderRadius: 1,
                                    }}
                                 />
                              </>
                           )}
                           <Button
                              variant='contained'
                              startIcon={<PhotoCameraIcon sx={{ fontSize: '1.25rem' }} />}
                              onClick={() => setShowUploader((prev) => !prev)}
                              sx={{
                                 fontSize: '0.875rem',
                                 py: 1,
                                 px: 2,
                                 borderRadius: 1,
                              }}
                           >
                              {showUploader ? 'Hide Uploader' : 'Add Photos'}
                           </Button>
                        </Box>
                     </Box>
                  )}

                  {showUploader && (
                     <Paper
                        sx={{
                           p: isMobile ? 2 : 3,
                           mb: isMobile ? 2 : 3,
                           borderRadius: isMobile ? 2 : 1,
                           boxShadow: isMobile ? 1 : 2,
                        }}
                     >
                        <TripPhotoUploader
                           tripId={tripId || ''}
                           tripLocation={trip?.primaryDestination || null}
                           onUploadComplete={handlePhotoUploadComplete}
                        />
                     </Paper>
                  )}

                  {loading ? (
                     <Box
                        sx={{
                           display: 'flex',
                           justifyContent: 'center',
                           p: isMobile ? 2 : 4,
                        }}
                     >
                        <CircularProgress size={isMobile ? 32 : 40} />
                     </Box>
                  ) : tripPhotos.length && !showUploader ? (
                     <Box
                        sx={{
                           width: '100%',
                           overflow: 'hidden',
                           px: isMobile ? 0 : 0,
                        }}
                     >
                        <Grid
                           container
                           spacing={isMobile ? 1 : 2}
                           sx={{
                              width: '100%',
                              margin: 0,
                              maxWidth: '100%',
                              '& .MuiGrid-item': {
                                 paddingLeft: isMobile ? '4px !important' : undefined,
                                 paddingTop: isMobile ? '4px !important' : undefined,
                                 maxWidth: isMobile ? 'calc(50% - 4px)' : undefined,
                              },
                           }}
                        >
                           {tripPhotos.map((photo: any) => (
                              <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2} key={photo.id}>
                                 <Card
                                    sx={{
                                       position: 'relative',
                                       borderRadius: isMobile ? '12px' : 1,
                                       overflow: 'hidden',
                                       boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 2,
                                       border: selectedPhotos.has(photo.id)
                                          ? '3px solid #1976d2'
                                          : isMobile
                                            ? '1px solid #f0f0f0'
                                            : 'none',
                                       '&:hover': {
                                          boxShadow: isMobile ? '0 4px 12px rgba(0,0,0,0.15)' : 4,
                                          transform: isMobile ? 'none' : 'translateY(-2px)',
                                          transition: 'all 0.2s ease-in-out',
                                       },
                                    }}
                                 >
                                    <CardMedia
                                       component='img'
                                       height={isMobile ? '140' : '140'}
                                       image={photo.url}
                                       alt={photo.title}
                                       sx={{
                                          objectFit: 'cover',
                                          cursor: 'pointer',
                                          opacity: selectedPhotos.has(photo.id) ? 0.8 : 1,
                                       }}
                                       onClick={() => window.open(photo.url, '_blank')}
                                    />

                                    {/* Selection checkbox - only visible for editors/owners */}
                                    {canShare && (
                                       <Box
                                          position='absolute'
                                          top={8}
                                          left={8}
                                          bgcolor={
                                             selectedPhotos.has(photo.id)
                                                ? '#1976d2'
                                                : 'rgba(0,0,0,0.6)'
                                          }
                                          borderRadius='50%'
                                          p={0.3}
                                          sx={{
                                             transition: 'all 0.2s ease',
                                             border: selectedPhotos.has(photo.id)
                                                ? '2px solid white'
                                                : '2px solid transparent',
                                             zIndex: 10,
                                          }}
                                       >
                                          <Checkbox
                                             checked={selectedPhotos.has(photo.id)}
                                             onChange={(e) => {
                                                e.stopPropagation();
                                                togglePhotoSelection(photo.id);
                                             }}
                                             size='small'
                                             sx={{
                                                color: 'white',
                                                p: 0,
                                                '&.Mui-checked': {
                                                   color: 'white',
                                                },
                                             }}
                                          />
                                       </Box>
                                    )}

                                    {(photo?.isTransformed || photo?.basePhotoId) && (
                                       <Typography
                                          sx={{
                                             position: 'absolute',
                                             bottom: 8,
                                             right: 8,
                                             backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                             padding: '5px 10px',
                                             borderRadius: '5px',
                                             zIndex: 5,
                                          }}
                                          variant='caption'
                                          color='secondary.main'
                                       >
                                          AI Generated
                                       </Typography>
                                    )}

                                    {!isMobile && (
                                       <Box
                                          sx={{
                                             position: 'absolute',
                                             top: 5,
                                             right: 5,
                                             display: 'flex',
                                             gap: 1,
                                             alignItems: 'center',
                                          }}
                                       >
                                          <Button
                                             sx={{
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                padding: '5px 10px',
                                                minHeight: 'max-content',
                                                height: 'max-content',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 0.5,
                                                '&:hover': {
                                                   backgroundColor: 'primary.dark',
                                                },
                                             }}
                                             onClick={() => {
                                                const photoIndex = tripPhotos.findIndex(
                                                   (p) => p.id === photo.id
                                                );
                                                setPhotoGalleryIndex(photoIndex);
                                                setPhotoGalleryOpen(true);
                                             }}
                                          >
                                             View
                                          </Button>
                                       </Box>
                                    )}

                                    <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                                       {isMobile ? (
                                          // Mobile: Column layout to prevent overlapping
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                             }}
                                          >
                                             <Typography
                                                variant='body2'
                                                sx={{
                                                   fontSize: '0.875rem',
                                                   fontWeight: 600,
                                                   color: 'text.primary',
                                                   overflow: 'hidden',
                                                   textOverflow: 'ellipsis',
                                                   whiteSpace: 'nowrap',
                                                }}
                                             >
                                                {photo?.title || 'Untitled'}
                                             </Typography>
                                             {photo.location && (
                                                <Typography
                                                   variant='body2'
                                                   color='text.secondary'
                                                   sx={{
                                                      fontSize: '0.75rem',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                   }}
                                                >
                                                   {photo.location?.name}
                                                </Typography>
                                             )}
                                             {/* Mobile: View button at bottom */}
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                sx={{
                                                   mt: 1,
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   borderRadius: '20px',
                                                   borderColor: '#00C4A7',
                                                   color: '#00C4A7',
                                                   '&:hover': {
                                                      borderColor: '#00C4A7',
                                                      backgroundColor: 'rgba(0, 196, 167, 0.04)',
                                                   },
                                                }}
                                                onClick={() => {
                                                   const photoIndex = tripPhotos.findIndex(
                                                      (p) => p.id === photo.id
                                                   );
                                                   setPhotoGalleryIndex(photoIndex);
                                                   setPhotoGalleryOpen(true);
                                                }}
                                             >
                                                View Photo
                                             </Button>
                                             {/* Mobile: Like button */}
                                             {currentUser && (
                                                <Box sx={{ mt: 0.5 }}>
                                                   <PhotoLikeButton
                                                      photoId={photo.id}
                                                      userId={currentUser.uid}
                                                      initialLikeCount={photo.likeCount || 0}
                                                      initialIsLiked={photo.isLiked || false}
                                                      size='small'
                                                      variant='compact'
                                                      showCount={true}
                                                      tripId={tripId}
                                                   />
                                                </Box>
                                             )}
                                          </Box>
                                       ) : (
                                          // Desktop: Original layout
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                             }}
                                          >
                                             {!photo?.isTransformed || !photo?.basePhotoId ? (
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                   <Typography
                                                      variant='caption'
                                                      sx={{
                                                         fontSize: '0.75rem',
                                                         fontWeight: 500,
                                                         display: 'block',
                                                         overflow: 'hidden',
                                                         textOverflow: 'ellipsis',
                                                         whiteSpace: 'nowrap',
                                                         color: 'text.primary',
                                                      }}
                                                   >
                                                      {photo?.title || 'Untitled'}
                                                   </Typography>
                                                   {photo.location && (
                                                      <Typography
                                                         variant='body2'
                                                         color='text.secondary'
                                                         sx={{
                                                            mt: 0.5,
                                                            fontSize: '0.75rem',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                         }}
                                                      >
                                                         {photo.location?.name}
                                                      </Typography>
                                                   )}
                                                   {photo.description && (
                                                      <Typography
                                                         variant='body2'
                                                         color='text.secondary'
                                                         sx={{ mt: 0.5, fontSize: '0.7rem' }}
                                                      >
                                                         {photo?.description?.slice(0, 20)}
                                                      </Typography>
                                                   )}
                                                   {hasUploaderMediaIds(
                                                      photo.userId,
                                                      photo.uploadedBy,
                                                   ) && (
                                                      <AlbumTimelineUploaderRow
                                                         photo={photo}
                                                         isMobile={isMobile}
                                                      />
                                                   )}
                                                </Box>
                                             ) : (
                                                <Box>
                                                   <Typography variant='caption'>
                                                      {photo?.title?.slice(0, 5)}
                                                   </Typography>
                                                   {photo.location && (
                                                      <Typography
                                                         variant='body2'
                                                         color='text.secondary'
                                                         sx={{ mt: 1 }}
                                                      >
                                                         {photo.location?.name}
                                                      </Typography>
                                                   )}
                                                   {photo.description && (
                                                      <Typography
                                                         variant='body2'
                                                         color='text.secondary'
                                                         sx={{ mt: 1 }}
                                                      >
                                                         {photo?.description?.slice(0, 20)}
                                                      </Typography>
                                                   )}
                                                </Box>
                                             )}

                                             <Box sx={{ display: 'flex', gap: 1 }}>
                                                {(!photo?.isTransformed || !photo?.basePhotoId) && (
                                                   <Tooltip title='Transform Style'>
                                                      <IconButton
                                                         color='primary'
                                                         sx={{ width: 24, height: 24 }}
                                                         onClick={() => {
                                                            setSelectedPhoto(photo);
                                                            setStyleModalOpen(true);
                                                         }}
                                                      >
                                                         <StyleIcon sx={{ fontSize: 16 }} />
                                                      </IconButton>
                                                   </Tooltip>
                                                )}
                                                {currentUser &&
                                                   photo.userId === currentUser.uid && (
                                                      <Tooltip title='Edit Photo'>
                                                         <IconButton
                                                            color='primary'
                                                            onClick={() =>
                                                               handleCloseEditDialog(photo)
                                                            }
                                                            sx={{
                                                               width: 24,
                                                               height: 24,
                                                               '&:hover': {
                                                                  backgroundColor: 'primary.light',
                                                                  color: 'primary.contrastText',
                                                               },
                                                            }}
                                                         >
                                                            <EditIcon sx={{ fontSize: 16 }} />
                                                         </IconButton>
                                                      </Tooltip>
                                                   )}
                                                {(isTripOwner ||
                                                   (currentUser &&
                                                      photo.uploadedBy === currentUser.uid)) && (
                                                   <Tooltip title='Delete Photo'>
                                                      <IconButton
                                                         color='error'
                                                         onClick={() => handleDeletePhoto(photo)}
                                                         sx={{
                                                            width: 24,
                                                            height: 24,
                                                            '&:hover': {
                                                               backgroundColor: 'error.light',
                                                               color: 'error.contrastText',
                                                            },
                                                         }}
                                                      >
                                                         <DeleteIcon sx={{ fontSize: 16 }} />
                                                      </IconButton>
                                                   </Tooltip>
                                                )}
                                                {currentUser && (
                                                   <PhotoLikeButton
                                                      photoId={photo.id}
                                                      userId={currentUser.uid}
                                                      initialLikeCount={photo.likeCount || 0}
                                                      initialIsLiked={photo.isLiked || false}
                                                      size='small'
                                                      variant='compact'
                                                      showCount={true}
                                                      tripId={tripId}
                                                   />
                                                )}
                                             </Box>
                                          </Box>
                                       )}
                                    </CardContent>
                                 </Card>
                              </Grid>
                           ))}
                        </Grid>
                     </Box>
                  ) : (
                     <Box
                        sx={{
                           height: 300,
                           bgcolor: 'action.hover',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           borderRadius: 1,
                        }}
                     >
                        <Box sx={{ textAlign: 'center' }}>
                           <PhotoCameraIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                           <Typography variant='body1' color='text.secondary' gutterBottom>
                              No photos yet
                           </Typography>
                           {isTripOwner && (
                              <Button
                                 variant='contained'
                                 startIcon={<PhotoCameraIcon />}
                                 onClick={() => setShowUploader(true)}
                              >
                                 Add Photos
                              </Button>
                           )}
                        </Box>
                     </Box>
                  )}
               </Box>
            )}

            {/* Map tab */}
            {activeTab === getTabIndices().map && (
               <Box sx={{ height: 400 }}>
                  <TripMapView
                     trip={
                        {
                           ...trip,
                           id: trip.id,
                           title: trip.title,
                           startDate: trip.startDate,
                           endDate: trip.endDate,
                           userId: trip.userId,
                           createdAt: new Date(),
                           updatedAt: new Date(),
                        } as TripsType
                     }
                     height='100%'
                  />
               </Box>
            )}

            {/* Albums tab */}
            {activeTab === getTabIndices().albums && (
               <Box>
                  {/* Mobile-optimized header for albums */}
                  {isMobile && (
                     <Box sx={{ mb: 2 }}>
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2,
                           }}
                        >
                           <Typography
                              variant='h6'
                              component='h2'
                              sx={{
                                 fontSize: '1.125rem',
                                 fontWeight: 600,
                                 color: 'text.primary',
                              }}
                           >
                              Trip Photo Albums
                           </Typography>
                           <Button
                              variant='contained'
                              size='small'
                              sx={{
                                 borderRadius: '12px',
                                 textTransform: 'none',
                                 fontSize: '0.875rem',
                                 fontWeight: 600,
                                 background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                                 '&:hover': {
                                    background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                                 },
                              }}
                           >
                              + Create Album
                           </Button>
                        </Box>
                     </Box>
                  )}

                  <LinkedAlbums
                     tripId={tripId}
                     userId={currentUser?.uid || ''}
                     userEmail={currentUser?.email || ''}
                     photos={tripPhotos.map((photo) => ({
                        id: photo.id,
                        url: photo.url,
                        thumbnailUrl: photo.url,
                        name: photo.title || photo.fileName || 'Untitled',
                        takenAt: photo.takenAt,
                        location: photo.location?.coordinates
                           ? {
                                latitude: photo.location.coordinates.latitude,
                                longitude: photo.location.coordinates.longitude,
                                address: photo.location.name,
                             }
                           : undefined,
                     }))}
                     title={isMobile ? '' : 'Trip Photo Albums'}
                  />
               </Box>
            )}
         </Box>

         <EditPhotoDialog
            open={showEditDialog}
            photo={selectedPhoto}
            onClose={() => setShowEditDialog(false)}
         />

         <StyleTransformModal
            open={styleModalOpen}
            onClose={() => setStyleModalOpen(false)}
            photoUrl={selectedPhoto?.url || ''}
            photoId={selectedPhoto?.id || ''}
            onSave={handleSave}
         />

         <Dialog
            open={openDeleteDialog}
            onClose={handleDeleteCancel}
            aria-labelledby='delete-dialog-title'
            aria-describedby='delete-dialog-description'
         >
            <DialogTitle id='delete-dialog-title'>Delete Photo?</DialogTitle>
            <DialogContent>
               <Typography id='delete-dialog-description'>
                  Are you sure you want to delete this photo? This action cannot be undone.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleDeleteCancel}>Cancel</Button>
               <Button onClick={handleDeleteConfirm} color='error' autoFocus>
                  Delete Photo
               </Button>
            </DialogActions>
         </Dialog>

         <Dialog
            open={openTransformedPhotoDeleteDialog}
            onClose={() => setOpenTransformedPhotoDeleteDialog(false)}
            aria-labelledby='delete-dialog-title'
            aria-describedby='delete-dialog-description'
         >
            <DialogTitle id='delete-dialog-title'>Delete Photo?</DialogTitle>
            <DialogContent>
               <Typography id='delete-dialog-description'>
                  Are you sure you want to delete this photo? This action cannot be undone.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleDeleteCancel}>Cancel</Button>
               <Button onClick={handleDeleteTransformedPhotoConfirm} color='error' autoFocus>
                  Delete Photo
               </Button>
            </DialogActions>
         </Dialog>

         <Dialog
            open={openAIVersion}
            onClose={() => setOpenAIVersion(false)}
            maxWidth='lg'
            fullWidth
         >
            <DialogTitle>AI Version</DialogTitle>
            <DialogContent dividers>
               <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                     <Typography variant='subtitle2' align='center' gutterBottom>
                        Original Photo
                     </Typography>
                     <Box
                        sx={{
                           background: `url(${selectedPhoto?.url || ''}) `,
                           backgroundSize: 'cover',
                           backgroundPosition: 'center',
                           margin: 'auto',
                           width: '100%',
                           height: 400,
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           border: '1px solid #eee',
                           borderRadius: 1,
                           padding: 0,
                        }}
                     ></Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                     <Typography variant='subtitle2' align='center' gutterBottom>
                        AI Version
                     </Typography>

                     {tripTransformedPhotos.length == 0 ? (
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '100%',
                           }}
                        >
                           <Typography variant='subtitle2' align='center' gutterBottom>
                              No AI Version
                           </Typography>
                        </Box>
                     ) : loadingTransformedPhotos ? (
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '100%',
                           }}
                        >
                           <CircularProgress />
                        </Box>
                     ) : (
                        <Carousel
                           indicators={false}
                           animation='slide'
                           navButtonsAlwaysVisible={true}
                           navButtonsProps={{
                              style: {
                                 backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                 borderRadius: '50%',
                                 padding: '10px',
                                 color: 'white',
                              },
                           }}
                           NextIcon={<ArrowForwardIos />}
                           PrevIcon={<ArrowBackIos />}
                           sx={{
                              width: '100%',
                              height: '400px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              '& .MuiPaper-root': {
                                 height: '100%',
                              },
                           }}
                        >
                           {tripTransformedPhotos?.map((photo: any) => (
                              <>
                                 <Box
                                    key={photo.id}
                                    sx={{
                                       background: `url(${photo?.url})  `,
                                       backgroundSize: 'cover',
                                       backgroundPosition: 'center',
                                       position: 'relative!important',
                                       margin: 'auto',
                                       width: '100%',
                                       height: '400px',
                                       display: 'flex',
                                       justifyContent: 'center',
                                       alignItems: 'center',
                                       padding: 0,
                                    }}
                                 >
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          gap: 1,
                                          top: 10,
                                          left: '50%',
                                          zIndex: 50,
                                          position: 'absolute',
                                          alignItems: 'center',
                                       }}
                                    >
                                       <Tooltip title='Delete AI Version'>
                                          <IconButton
                                             sx={{
                                                width: '20px',
                                                height: '20px',
                                                backgroundColor: 'green',
                                                borderRadius: '50%',
                                                '&:hover': {
                                                   backgroundColor: 'green!important',
                                                },
                                             }}
                                             onClick={() => {
                                                handleTransformedPhotoDelete(photo);
                                             }}
                                          >
                                             <DeleteIcon
                                                sx={{
                                                   color: 'white',
                                                   width: '15px',
                                                   height: '15px',
                                                }}
                                                fontSize='small'
                                             />
                                          </IconButton>
                                       </Tooltip>
                                    </Box>
                                 </Box>
                              </>
                           ))}
                        </Carousel>
                     )}
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setOpenAIVersion(false)}>Close</Button>
            </DialogActions>
         </Dialog>

         <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={() => setSnackbarOpen(false)}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>

         {/* Edit Trip Form Dialog */}
         {trip && editMode && (
            <EditTripForm
               open={editMode}
               onClose={handleEditClose}
               tripId={trip.id}
               initialData={{
                  title: trip.title,
                  description: trip.description || '',
                  startDate: trip.startDate,
                  endDate: trip.endDate,
                  primaryDestination: trip.primaryDestination,
                  accommodations: trip.accommodations?.map((acc) => ({
                     name: acc.name,
                     address: acc.address,
                     checkIn: acc.checkIn,
                     checkOut: acc.checkOut,
                     coordinates: acc.coordinates,
                  })),
               }}
            />
         )}

         {/* View All Photos Modal */}
         <Dialog
            open={viewAllPhotosOpen}
            onClose={handleCloseViewAllPhotos}
            maxWidth={isMobile ? 'sm' : 'xl'}
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
               sx: {
                  height: isMobile ? '100vh' : '90vh',
                  maxHeight: isMobile ? '100vh' : '90vh',
                  m: isMobile ? 0 : 2,
                  borderRadius: isMobile ? 0 : 1,
               },
            }}
         >
            <DialogTitle sx={{ p: isMobile ? 2 : 3 }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography
                     variant={isMobile ? 'h6' : 'h5'}
                     sx={{ fontSize: isMobile ? '1.1rem' : '1.5rem' }}
                  >
                     All Trip Photos ({tripPhotos.length})
                  </Typography>
                  <IconButton
                     onClick={handleCloseViewAllPhotos}
                     sx={{
                        p: isMobile ? 1 : 1.5,
                        '&:hover': { bgcolor: 'action.hover' },
                     }}
                  >
                     <CloseIcon sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
                  </IconButton>
               </Box>
            </DialogTitle>

            <DialogContent
               sx={{
                  p: isMobile ? 1 : 2,
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                     width: isMobile ? '4px' : '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                     background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                     background: 'rgba(0,0,0,0.2)',
                     borderRadius: '4px',
                  },
               }}
            >
               {loadingPhotos ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                     <CircularProgress />
                  </Box>
               ) : (
                  <Grid container spacing={isMobile ? 1.5 : 2}>
                     {currentPhotos.map((photo: any) => (
                        <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2} key={photo.id}>
                           <Card
                              sx={{
                                 position: 'relative',
                                 transition: 'transform 0.2s ease-in-out',
                                 borderRadius: isMobile ? 1.5 : 1,
                                 border: selectedPhotos.has(photo.id)
                                    ? '3px solid #1976d2'
                                    : 'none',
                                 '&:hover': {
                                    transform: isMobile ? 'none' : 'scale(1.02)',
                                    boxShadow: isMobile ? 2 : 3,
                                 },
                              }}
                           >
                              <CardMedia
                                 component='img'
                                 height={isMobile ? '120' : '140'}
                                 image={photo.url}
                                 alt={photo.title}
                                 sx={{
                                    objectFit: 'cover',
                                    cursor: 'pointer',
                                    opacity: selectedPhotos.has(photo.id) ? 0.8 : 1,
                                 }}
                                 onClick={() => window.open(photo.url, '_blank')}
                              />

                              {/* Selection checkbox - only visible for editors/owners */}
                              {canShare && (
                                 <Box
                                    position='absolute'
                                    top={8}
                                    left={8}
                                    bgcolor={
                                       selectedPhotos.has(photo.id) ? '#1976d2' : 'rgba(0,0,0,0.6)'
                                    }
                                    borderRadius='50%'
                                    p={0.3}
                                    sx={{
                                       transition: 'all 0.2s ease',
                                       border: selectedPhotos.has(photo.id)
                                          ? '2px solid white'
                                          : '2px solid transparent',
                                    }}
                                 >
                                    <Checkbox
                                       checked={selectedPhotos.has(photo.id)}
                                       onChange={(e) => {
                                          e.stopPropagation();
                                          togglePhotoSelection(photo.id);
                                       }}
                                       size='small'
                                       sx={{
                                          color: 'white',
                                          p: 0,
                                          '&.Mui-checked': {
                                             color: 'white',
                                          },
                                       }}
                                    />
                                 </Box>
                              )}

                              {/* Selection count badge */}
                              {selectedPhotos.has(photo.id) && (
                                 <Box
                                    position='absolute'
                                    top={8}
                                    left={8}
                                    bgcolor='#1976d2'
                                    color='white'
                                    borderRadius='50%'
                                    width={24}
                                    height={24}
                                    display='flex'
                                    alignItems='center'
                                    justifyContent='center'
                                    sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                                 >
                                    {Array.from(selectedPhotos).indexOf(photo.id) + 1}
                                 </Box>
                              )}

                              {(photo?.isTransformed || photo?.basePhotoId) && (
                                 <Typography
                                    sx={{
                                       position: 'absolute',
                                       bottom: 8,
                                       right: 8,
                                       backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                       color: 'white',
                                       padding: '4px 8px',
                                       borderRadius: '4px',
                                       fontSize: '0.75rem',
                                    }}
                                 >
                                    AI Generated
                                 </Typography>
                              )}

                              <CardContent sx={{ p: 2 }}>
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       justifyContent: 'space-between',
                                       alignItems: 'flex-start',
                                       mb: 1,
                                    }}
                                 >
                                    <Typography variant='subtitle2' noWrap sx={{ flex: 1, mr: 1 }}>
                                       {photo?.title || 'Untitled'}
                                    </Typography>
                                    {currentUser && (
                                       <PhotoLikeButton
                                          photoId={photo.id}
                                          userId={currentUser.uid}
                                          initialLikeCount={photo.likeCount || 0}
                                          initialIsLiked={photo.isLiked || false}
                                          size='small'
                                          variant='compact'
                                          showCount={true}
                                          tripId={tripId}
                                       />
                                    )}
                                 </Box>
                                 {photo.location && (
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          mt: 0.5,
                                       }}
                                    >
                                       <LocationOnIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                       {photo.location?.name}
                                    </Typography>
                                 )}
                                 {photo.description && (
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       sx={{
                                          mt: 1,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                       }}
                                    >
                                       {photo.description}
                                    </Typography>
                                 )}
                                 {hasUploaderMediaIds(photo.userId, photo.uploadedBy) && (
                                    <AlbumTimelineUploaderRow
                                       photo={photo}
                                       isMobile={isMobile}
                                    />
                                 )}
                              </CardContent>
                           </Card>
                        </Grid>
                     ))}
                  </Grid>
               )}
            </DialogContent>

            <DialogActions
               sx={{
                  p: isMobile ? 2 : 3,
                  flexDirection: 'column',
                  gap: isMobile ? 1.5 : 2,
               }}
            >
               {/* Pagination Controls */}
               {totalPages > 1 && (
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: isMobile ? 1 : 2,
                        width: '100%',
                        flexWrap: 'wrap',
                     }}
                  >
                     <Button
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        startIcon={<ArrowBackIos />}
                        size={isMobile ? 'medium' : 'small'}
                        sx={{
                           fontSize: isMobile ? '0.8rem' : 'inherit',
                           px: isMobile ? 2 : 1.5,
                        }}
                     >
                        {isMobile ? 'Prev' : 'Previous'}
                     </Button>

                     <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => handlePageChange(page)}
                        color='primary'
                        size={isMobile ? 'small' : 'large'}
                        showFirstButton={!isMobile}
                        showLastButton={!isMobile}
                        siblingCount={isMobile ? 0 : 1}
                        boundaryCount={isMobile ? 1 : 1}
                     />

                     <Button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        endIcon={<ArrowForwardIos />}
                        size={isMobile ? 'medium' : 'small'}
                        sx={{
                           fontSize: isMobile ? '0.8rem' : 'inherit',
                           px: isMobile ? 2 : 1.5,
                        }}
                     >
                        {isMobile ? 'Next' : 'Next'}
                     </Button>
                  </Box>
               )}

               {/* Page Info and Close Button */}
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     width: '100%',
                  }}
               >
                  <Typography variant='body2' color='text.secondary'>
                     Showing {startIndex + 1}-{Math.min(endIndex, tripPhotos.length)} of{' '}
                     {tripPhotos.length} photos
                  </Typography>
                  <Button onClick={handleCloseViewAllPhotos} variant='outlined'>
                     Close
                  </Button>
               </Box>
            </DialogActions>
         </Dialog>

         {/* Participant Profile Modal */}
         <ParticipantProfileModal
            open={participantProfileModalOpen}
            onClose={handleCloseParticipantProfile}
            participant={selectedParticipant as any}
         />

         {/* Photo Gallery Popup */}
         <PhotoGalleryPopup
            open={photoGalleryOpen}
            onClose={() => setPhotoGalleryOpen(false)}
            photos={(tripPhotos || []).map((photo) => ({
               ...photo,
               filename: (photo as any).filename || photo.title || 'Unknown',
               thumbnailUrl: (photo as any).thumbnailUrl || photo.url,
               title: photo.title || 'Untitled',
               description: photo.description || '',
               tags: photo.tags || [],
               takenAt:
                  photo.takenAt instanceof Date
                     ? photo.takenAt
                     : (photo.takenAt as any)?.toDate
                       ? (photo.takenAt as any).toDate()
                       : new Date(),
               uploadedAt:
                  photo.uploadedAt instanceof Date
                     ? photo.uploadedAt
                     : (photo.uploadedAt as any)?.toDate
                       ? (photo.uploadedAt as any).toDate()
                       : new Date(),
            }))}
            initialPhotoIndex={photoGalleryIndex}
         />
      </Box>
   );
};

export default TripDetailPage;
